/**
 * OverviewPage — `/overview`. Сводка по продвижению KOMMA.
 *
 * KPI-барометр (CPL / расход / заявки / активные кампании) + тренд CPL + сплит
 * по доменам с явной пометкой слепой зоны (call.komma.software — нет счётчика).
 * Данные — /api/reports/overview (кросс-счётчиковый CPL на бэке).
 */

import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { usePageHeader } from '@/components/layout/pageHeader';
import { BaseChart } from '@/components/charts/BaseChart';
import { PeriodPicker, type Period } from '@/components/ui/PeriodPicker';
import { TodayDecisions } from '@/components/ai/TodayDecisions';
import { Badge, LoadingBlock, ErrorBlock } from '@/shared/ui';
import { chartTokens, baseChart, axisStyle } from '@/lib/echarts';
import { useOverview, type DomainBreakdown } from '@/hooks/api/useReports';
import { useDealsTrend } from '@/hooks/api/useDealsTrend';
import { useDeals, type DealListItem, type DealStatus } from '@/hooks/api/useDeals';
import type { EChartsOption } from 'echarts';

const DEAL_TONE: Record<DealStatus, 'b-success' | 'b-danger' | 'b-neutral'> = {
  won: 'b-success',
  lost: 'b-danger',
  active: 'b-neutral',
};

function shortDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function rub(value: number | null | undefined): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(value);
}

function num(value: number | null | undefined): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('ru-RU').format(value);
}

export function OverviewPage() {
  usePageHeader({ title: 'Обзор', subtitle: 'Сводка по продвижению KOMMA' });

  const [period, setPeriod] = useState<Period>({ days: 10 });
  const { data, isLoading, isError, error, refetch } = useOverview(period);
  const deals = useDealsTrend(period);
  const dealsList = useDeals(period, 20);

  const dealsOption = useMemo<EChartsOption>(() => {
    const t = chartTokens();
    const tr = deals.data?.trend ?? [];
    return {
      ...baseChart(t),
      tooltip: { trigger: 'axis' },
      legend: { data: ['Сделки', 'Выиграно'], top: 0, right: 0 },
      grid: { left: 48, right: 24, top: 28, bottom: 36 },
      xAxis: { type: 'category', data: tr.map((p) => p.date), ...axisStyle(t) },
      yAxis: { type: 'value', name: 'Сделок', minInterval: 1, ...axisStyle(t) },
      series: [
        {
          name: 'Сделки',
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: tr.map((p) => p.deals),
          areaStyle: { opacity: 0.08 },
          lineStyle: { width: 2.5 },
          color: t.primary,
        },
        {
          name: 'Выиграно',
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: tr.map((p) => p.won),
          lineStyle: { width: 2 },
          color: t.success,
        },
      ],
    };
  }, [deals.data]);

  const trendOption = useMemo<EChartsOption>(() => {
    const t = chartTokens();
    const trend = data?.trend ?? [];
    return {
      ...baseChart(t),
      tooltip: { trigger: 'axis' },
      grid: { left: 56, right: 24, top: 24, bottom: 36 },
      xAxis: { type: 'category', data: trend.map((p) => p.date), ...axisStyle(t) },
      yAxis: { type: 'value', name: 'CPL ₽', ...axisStyle(t) },
      series: [
        {
          name: 'CPL',
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: trend.map((p) => p.cpl),
          areaStyle: { opacity: 0.08 },
          lineStyle: { width: 2.5 },
          markLine: data?.kpis.cplCap
            ? {
                silent: true,
                symbol: 'none',
                label: { formatter: 'Лимит CPL', position: 'insideEndTop' },
                lineStyle: { type: 'dashed', color: t.error },
                data: [{ yAxis: data.kpis.cplCap }],
              }
            : undefined,
        },
      ],
    };
  }, [data]);

  const clusterOption = useMemo<EChartsOption>(() => {
    const t = chartTokens();
    const cap = data?.kpis.cplCap ?? 15000;
    const cl = (data?.clusters ?? []).filter((c) => c.cpl != null);
    return {
      ...baseChart(t),
      tooltip: { trigger: 'axis' },
      grid: { left: 8, right: 60, top: 8, bottom: 8, containLabel: true },
      xAxis: { type: 'value', name: 'CPL ₽', ...axisStyle(t) },
      yAxis: { type: 'category', data: cl.map((c) => c.cluster), inverse: true, ...axisStyle(t) },
      series: [
        {
          type: 'bar',
          data: cl.map((c) => ({
            value: Math.round(c.cpl ?? 0),
            itemStyle: { color: (c.cpl ?? 0) <= cap ? t.success : t.error },
          })),
          barWidth: '58%',
          label: { show: true, position: 'right', formatter: '{c} ₽' },
        },
      ],
    };
  }, [data]);

  const kpis = data?.kpis;
  const cplOk = kpis?.cpl != null && kpis.cpl <= kpis.cplCap;

  return (
    <div className="col gap20" style={{ maxWidth: 1200 }}>
      <div className="row spread">
        {data?.source === 'stub' ? (
          <Badge tone="b-warning">демо-данные · добавьте YANDEX_DIRECT_TOKEN</Badge>
        ) : (
          <span />
        )}
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>

      {isLoading && <LoadingBlock label="Загружаю метрики…" />}
      {isError && <ErrorBlock text={error?.message} onRetry={() => void refetch()} />}
      {!isLoading && !isError && data && kpis && (
      <>
      <div className="stat-grid">
        <Stat label="CPL (цена MQL)" value={rub(kpis.cpl)}
          delta={kpis.cpl != null ? (cplOk ? 'в норме' : 'выше лимита') : 'нет данных'}
          tone={kpis.cpl == null ? 'neutral' : cplOk ? 'success' : 'danger'} />
        <Stat label="Расход" value={rub(kpis.spend)} />
        <Stat label="Заявки (форм-цели)" value={num(kpis.leads)} />
        <Stat label="Активные кампании" value={num(kpis.activeCampaigns)} />
      </div>

      <section
        className="card row spread wrap"
        style={{ gap: 16, borderColor: data.money.burned > 0 ? 'var(--danger)' : 'var(--border)' }}
      >
        <div className="col" style={{ gap: 2 }}>
          <span className="muted" style={{ fontSize: 12.5, fontWeight: 700 }}>🔴 Теряем (сожжённый бюджет)</span>
          <span style={{ fontSize: 26, fontWeight: 800, color: 'var(--danger-fg)' }}>{rub(data.money.burned)}</span>
          <span className="muted" style={{ fontSize: 12 }}>активные кампании с 0 заявок за период</span>
        </div>
        <div className="col" style={{ gap: 2 }}>
          <span className="muted" style={{ fontSize: 12.5, fontWeight: 700 }}>🟢 Потенциал выручки</span>
          <span style={{ fontSize: 26, fontWeight: 800, color: 'var(--success-fg)' }}>
            +{data.money.potentialLeads} лид.
          </span>
          <span className="muted" style={{ fontSize: 12 }}>
            если перелить в лидеров (CPL ~{rub(data.money.leaderCpl)})
          </span>
        </div>
        <div className="col center" style={{ justifyContent: 'center' }}>
          <span className="muted" style={{ fontSize: 12.5, maxWidth: 240 }}>
            Сократить потери и добрать лиды — в блоке «Решения дня» ниже.
          </span>
        </div>
      </section>

      <TodayDecisions period={period} />

      <section className="card">
        <div className="row spread" style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Динамика CPL</h3>
          <span className="muted mono" style={{ fontSize: 12 }}>
            {data.period.from} — {data.period.to} · {data.period.days} дн.
          </span>
        </div>
        <BaseChart option={trendOption} height={300} isEmpty={!data.trend.length}
          emptyText="Нет данных за период" ariaLabel="Динамика CPL" />
      </section>

      <section className="card">
        <div className="row spread" style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Динамика сделок</h3>
          {deals.data?.configured && (
            <span className="muted mono" style={{ fontSize: 12 }}>
              всего {num(deals.data.total)} · выиграно {num(deals.data.won)}
            </span>
          )}
        </div>
        {deals.data && !deals.data.configured ? (
          <span className="muted" style={{ fontSize: 13 }}>
            CRM (Bitrix24) не подключён — нет данных по сделкам.
          </span>
        ) : (
          <BaseChart
            option={dealsOption}
            height={300}
            isEmpty={!deals.data?.trend?.some((p) => p.deals > 0)}
            emptyText={deals.isLoading ? 'Загружаю сделки…' : 'Нет сделок за период'}
            ariaLabel="Динамика сделок"
          />
        )}
      </section>

      {dealsList.data?.configured && (
        <section className="card">
          <div className="row spread" style={{ marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>Последние сделки</h3>
            <span className="muted mono" style={{ fontSize: 12 }}>
              {dealsList.data.total != null ? `всего ${num(dealsList.data.total)} за период` : ''}
            </span>
          </div>
          {dealsList.isLoading ? (
            <LoadingBlock label="Загружаю сделки…" />
          ) : (dealsList.data.deals?.length ?? 0) === 0 ? (
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>Нет сделок за период.</p>
          ) : (
            <>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Сделка</th>
                    <th>Стадия</th>
                    <th style={{ textAlign: 'right' }}>Сумма</th>
                    <th>Источник / кампания</th>
                  </tr>
                </thead>
                <tbody>
                  {dealsList.data.deals?.map((d) => (
                    <DealRow key={d.id} d={d} />
                  ))}
                </tbody>
              </table>
              <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
                Клик по названию — карточка сделки (что за заявка, из какой кампании, контакт).
              </p>
            </>
          )}
        </section>
      )}

      <section className="card">
        <h3 style={{ marginTop: 0 }}>CPL по кластерам</h3>
        <BaseChart
          option={clusterOption}
          height={Math.max(180, (data.clusters.filter((c) => c.cpl != null).length || 1) * 46)}
          isEmpty={!data.clusters.some((c) => c.cpl != null)}
          emptyText="Нет конверсий по кластерам за период"
          ariaLabel="CPL по кластерам"
        />
      </section>

      <section className="card">
        <h3 style={{ marginTop: 0 }}>Сплит по доменам</h3>
        <table className="tbl">
          <thead>
            <tr>
              <th>Домен</th>
              <th style={{ textAlign: 'right' }}>Расход</th>
              <th style={{ textAlign: 'right' }}>Заявки</th>
              <th style={{ textAlign: 'right' }}>CPL</th>
              <th>Трекинг</th>
            </tr>
          </thead>
          <tbody>
            {data.domains.map((d) => (
              <DomainRow key={d.domain} d={d} />
            ))}
          </tbody>
        </table>
      </section>
      </>
      )}
    </div>
  );
}

function DealRow({ d }: { d: DealListItem }) {
  return (
    <tr>
      <td className="mono" style={{ whiteSpace: 'nowrap', fontSize: 12.5 }}>{shortDate(d.dateCreate)}</td>
      <td>
        <Link to="/deals/$id" params={{ id: d.id }} style={{ color: 'var(--primary)', fontWeight: 600 }}>
          {d.title || `Сделка #${d.id}`}
        </Link>
      </td>
      <td>
        <Badge tone={DEAL_TONE[d.status]}>{d.stageName}</Badge>
      </td>
      <td className="tnum" style={{ textAlign: 'right' }}>{d.opportunity ? rub(d.opportunity) : '—'}</td>
      <td className="muted" style={{ fontSize: 12 }}>
        {d.utmSource || '—'}
        {d.utmCampaign ? ` · ${d.utmCampaign}` : ''}
      </td>
    </tr>
  );
}

function DomainRow({ d }: { d: DomainBreakdown }) {
  return (
    <tr>
      <td className="mono">{d.domain}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{rub(d.spend)}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{d.leads == null ? '—' : num(d.leads)}</td>
      <td className="tnum" style={{ textAlign: 'right' }}>{rub(d.cpl)}</td>
      <td>
        {d.tracked ? (
          <Badge tone="b-success">есть счётчик</Badge>
        ) : (
          <span className="row center gap8">
            <Badge tone="b-danger">слепая зона</Badge>
            {d.note && <span className="muted" style={{ fontSize: 12 }}>{d.note}</span>}
          </span>
        )}
      </td>
    </tr>
  );
}

function Stat({
  label,
  value,
  delta,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  delta?: string;
  tone?: 'success' | 'danger' | 'neutral';
}) {
  const deltaColor =
    tone === 'success' ? 'var(--success-fg)' : tone === 'danger' ? 'var(--danger-fg)' : 'var(--text-muted)';
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value">{value}</div>
      {delta && (
        <div className="stat__delta" style={{ color: deltaColor }}>
          {delta}
        </div>
      )}
    </div>
  );
}
